'use client'

import type { BulletPointsBlock as BulletPointsBlockProps } from 'src/payload-types'

import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import RichText from '@/components/RichText'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { Mask } from '@/components/Mask'
import Link from 'next/link'
import { getLink } from '@/utilities/getLink'

type Props = {
  className?: string
} & BulletPointsBlockProps

export const BulletPointsBlock: React.FC<Props> = ({ title, items, columns }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const paragraphVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
    }),
  }

  return (
    <section className="bg-white py-12 md:py-24">
      <div className="container w-full px-6">
        <div className="flex justify-center">
          <Mask width={50} height={50} color="black" />
        </div>
        <motion.div ref={ref} className="mx-auto w-full text-center">
          {title && (
            <>
              <AnimatedText
                text={title}
                type="h2"
                variant="simple"
                className="mb-8 font-heading text-2xl font-medium tracking-wide text-gray-900 md:text-4xl"
                delay={0.2}
              />
            </>
          )}

          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="mx-auto mb-10 h-px w-16 bg-antonie-red"
          />

          <div
            className={`grid grid-cols-1 gap-8 md:grid-cols-${columns === 1 ? '1' : '2'} lg:grid-cols-${columns}`}
          >
            {items.map((item, index) => (
              <div key={index} className="flex flex-col gap-4">
                <h3 className="text-left text-lg font-bold">{item.title}</h3>
                <motion.div
                  custom={index}
                  variants={paragraphVariants}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                >
                  <RichText
                    data={item.description}
                    enableGutter={false}
                    enableProse={false}
                    className="text-left!"
                  />
                </motion.div>
                {item.hasLink && (
                  <div className="mt-10 flex justify-center">
                    <Link href={getLink(item.link)} target={item.link?.newTab ? '_blank' : '_self'}>
                      <button className="group relative inline-flex items-center overflow-hidden border-2 border-antonie-green px-8 py-3 text-base font-light text-black transition-all duration-300 hover:bg-antonie-green hover:text-black">
                        <span className="relative z-10">{item.link?.label}</span>
                        <div className="absolute inset-0 -z-10 h-full w-full scale-x-0 transform bg-antonie-green transition-transform duration-300 group-hover:scale-x-100" />
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
